import { Paper, Typography } from '@material-ui/core';
import type {
  CockpitArtifactView,
  CockpitSectionViewState,
} from '../model/cockpitViewModel';
import {
  coverageLabel,
  limitationLabel,
  sectionStatusLabel,
} from '../model/semanticLabels';
import type { ArtifactContentAccess } from './ArtifactJourneyItem';
import { ArtifactJourneyItem } from './ArtifactJourneyItem';
import { CockpitSectionHead } from './CockpitSectionHead';
import { useCockpitStyles } from './cockpitStyles';

export type ArtifactsSectionProps = ArtifactContentAccess & {
  artifacts: CockpitArtifactView[];
  sectionState: CockpitSectionViewState;
  selectedId: string | null;
  onSelect: (id: string) => void;
};

const ArtifactJourneyList = ({
  artifacts,
  selectedId,
  onSelect,
  ...content
}: Omit<ArtifactsSectionProps, 'sectionState'>) => {
  const classes = useCockpitStyles();
  return (
    <ol
      className={classes.artifactSequence}
      aria-label="Ordem observada de geração dos artefatos"
    >
      {artifacts.map((artifact, index) => (
        <ArtifactJourneyItem
          key={artifact.id}
          artifact={artifact}
          index={index}
          total={artifacts.length}
          open={selectedId === artifact.id}
          onSelect={onSelect}
          {...content}
        />
      ))}
    </ol>
  );
};

export const ArtifactsSection = ({
  artifacts,
  sectionState,
  ...rest
}: ArtifactsSectionProps) => {
  const classes = useCockpitStyles();
  const visibleArtifacts = uniqueArtifacts(artifacts);
  return (
    <Paper
      component="section"
      className={classes.panel}
      aria-labelledby="artifact-sequence-title"
    >
      <CockpitSectionHead
        id="artifact-sequence-title"
        title="Artefatos gerados"
      >
        {sectionStatusLabel(sectionState.status)} ·{' '}
        {coverageLabel(sectionState.coverage)}. Em ordem de geração; abra um
        artefato para ler o conteúdo autorizado.
      </CockpitSectionHead>
      {visibleArtifacts.length === 0 ? (
        <Typography>Nenhum artefato foi observado nesta revisão.</Typography>
      ) : (
        <ArtifactJourneyList artifacts={visibleArtifacts} {...rest} />
      )}
      {sectionState.limitations.length > 0 && (
        <Typography variant="caption">
          Limitações: {sectionState.limitations.map(limitationLabel).join('; ')}
        </Typography>
      )}
    </Paper>
  );
};

function uniqueArtifacts(
  artifacts: CockpitArtifactView[],
): CockpitArtifactView[] {
  const identities = new Set<string>();
  return artifacts.filter(artifact => {
    if (identities.has(artifact.id)) return false;
    identities.add(artifact.id);
    return true;
  });
}
